import { createReferencePointIcon } from "@/components/Map/Icons/MarkerIcon";
import type { ReferencePoint } from "@/interfaces";
import L from "leaflet";
import { useEffect, useRef } from "react";

interface ReferencePointMarkerProps {
  referencePoint: ReferencePoint;
  isSelected: boolean;
  isInteractionDisabled: boolean;
  onReferencePointSelect: (referencePointId: number) => void;
  layerGroup: L.LayerGroup;
}

const calculatePolygonCenter = (
  polygonCoordinates: string,
): { lat: number; lng: number } | null => {
  const coordinates = polygonCoordinates.split(";");
  let totalLat = 0;
  let totalLng = 0;
  let validPoints = 0;

  for (const coord of coordinates) {
    const [lat, lng] = coord.split(",").map(Number);
    if (!isNaN(lat) && !isNaN(lng)) {
      totalLat += lat;
      totalLng += lng;
      validPoints++;
    }
  }

  if (validPoints > 0) {
    return {
      lat: totalLat / validPoints,
      lng: totalLng / validPoints,
    };
  }
  return null;
};

const getReferencePointCoordinates = (
  referencePoint: ReferencePoint,
): { lat: number; lng: number } | null => {
  const centerLat = referencePoint.center_latitude;
  const centerLng = referencePoint.center_longitude;

  if (centerLat && centerLng && centerLat !== "" && centerLng !== "") {
    const lat = Number(centerLat);
    const lng = Number(centerLng);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }

  if (referencePoint.polygon_coordinates) {
    return calculatePolygonCenter(referencePoint.polygon_coordinates);
  }

  return null;
};

const ReferencePointMarker: React.FC<ReferencePointMarkerProps> = ({
  referencePoint,
  isSelected,
  isInteractionDisabled,
  onReferencePointSelect,
  layerGroup,
}) => {
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    const coordinates = getReferencePointCoordinates(referencePoint);
    if (!coordinates) return;

    const { lat, lng } = coordinates;

    const markerIcon = createReferencePointIcon(
      isSelected,
      isInteractionDisabled,
    );

    const marker = L.marker([lat, lng], {
      icon: markerIcon,
    });

    const popupContent = `
      <div style="min-width: 250px; font-family: system-ui, -apple-system, sans-serif;">
        <div style="
          background: linear-gradient(135deg, #059669, #10B981);
          color: white;
          padding: 12px;
          margin: -12px -12px 12px -12px;
          border-radius: 8px 8px 0 0;
          text-align: center;
        ">
          <h3 style="margin: 0; font-size: 16px; font-weight: bold;">
            Ponto de Início
          </h3>
          ${
            !isInteractionDisabled
              ? `
            <p style="margin: 4px 0 0 0; font-size: 12px; opacity: 0.9;">
              ${isSelected ? "Clique para remover" : "Clique para selecionar"}
            </p>
          `
              : ""
          }
        </div>
        <div style="padding: 0 4px;">
          <p style="margin: 8px 0; font-size: 15px; font-weight: 600; color: #1F2937;">
            ${referencePoint.name}
          </p>
          <p style="margin: 6px 0; font-size: 13px; color: #4B5563;">
            <strong>Coordenadas:</strong> ${lat.toFixed(6)}, ${lng.toFixed(6)}
          </p>
          ${
            isSelected
              ? `
            <div style="margin: 8px 0; padding: 8px; background: #F3F4F6; border-radius: 6px;">
              <p style="margin: 0; font-size: 12px; color: #059669; font-weight: 600;">
                Selecionado como ponto de partida
              </p>
            </div>
          `
              : ""
          }
        </div>
      </div>
    `;

    marker.bindPopup(popupContent);

    if (!isInteractionDisabled) {
      marker.on("click", () => {
        onReferencePointSelect(referencePoint.id);
      });
    }

    layerGroup.addLayer(marker);
    markerRef.current = marker;

    return () => {
      if (markerRef.current) {
        layerGroup.removeLayer(markerRef.current);
      }
    };
  }, [
    referencePoint,
    isSelected,
    isInteractionDisabled,
    onReferencePointSelect,
    layerGroup,
  ]);

  return null;
};

export default ReferencePointMarker;
